import type { HTMLAttributes, ReactNode } from 'react';

import { PAGE_GUTTER } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/**
 * THE page measure. 1280px — the width `/discover` already used, and the width
 * every route now renders.
 *
 * This constant is the ONLY place in `src/` that may name a route-level
 * `max-w-*`. Anything that needs the page column — a route, a layout-matched
 * skeleton, the content board's shell — imports it rather than restating it.
 * Guarded by `src/components/layout/__tests__/page-measure.guard.test.ts`.
 *
 * ⚠️ NOT yet shared with `PageHeader`, deliberately. See the note at that
 * file's header-row `className` for the measurement and why a global cap there
 * was tried and reverted.
 *
 * Founder, 2026-08-26: _"some of our pages are just too narrow, there's too much
 * white space on each side… at least make them the same max width as the
 * Discover page."_ Then, 2026-08-27, after the default was flipped and 16 call
 * sites were left restating it: _"every page should not control its own max
 * width. That should be sized in whatever parent container it is in."_
 */
export const PAGE_MEASURE = 'max-w-7xl';

/**
 * Owns route-level centering, max-width, and horizontal gutter only.
 *
 * **There is no `width` prop and there must not be one.** It existed with four
 * values; on 2026-08-27 an inventory found 16 call sites passing `wide`, which
 * was by then identical to the default, and ZERO passing any of the other
 * three. Every one of the 16 was a no-op that a future edit could quietly turn
 * into a page-specific measure — which is the drift the founder was pointing at.
 * A page supplies content and vertical rhythm; the frame supplies the measure.
 *
 * If a route genuinely needs a different measure, change `PAGE_MEASURE` — in
 * one place, for every page at once — rather than giving one route an exception.
 *
 * ⚠️ `PAGE_MEASURE` is passed to `cn()` AFTER `className` on purpose.
 * tailwind-merge resolves a conflict in favour of the LAST class, so a caller
 * that passes `max-w-5xl` in `className` is ignored instead of silently
 * overriding the frame. The vertical rhythm and everything else in `className`
 * still applies.
 */
export function PageFrame({
  children,
  className,
  ...attributes
}: {
  children: ReactNode;
  className?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, 'className'>): JSX.Element {
  return (
    <div {...attributes} className={cn('mx-auto w-full', PAGE_GUTTER, className, PAGE_MEASURE)}>
      {children}
    </div>
  );
}
